import * as React from 'react'
import { useAtom } from 'infrastructure/state/jotai.wrapper'
import { useEdGateProfile, useEdGateStandards } from './hooks'
import { edgateSubjectAtom, edgateStandardsAtom } from './atoms'
import { renderLoading, renderMessage } from './utility'
import { EdgateStandardsView } from './EdgateStandardsView'

interface EdgateStandardsProps {
  state: string
  kiddomSubject: string
}

export const EdgateStandards: React.FC<EdgateStandardsProps> = ({
  state,
  kiddomSubject,
}) => {
  const { profile, loadingProfile, fetchProfile } = useEdGateProfile()
  const { loadingEdgateStandards, fetchEdgateStandards } = useEdGateStandards()
  const [edgateSubject, setEdgateSubject] = useAtom(edgateSubjectAtom)
  const [edgateStandards, setEdgateStandards] = useAtom(edgateStandardsAtom)

  // Fetch profile on mount
  React.useEffect(() => {
    if (!profile && !loadingProfile) {
      fetchProfile()
    }
  }, [profile, loadingProfile, fetchProfile])

  // Clear data when state or subject changes
  React.useEffect(() => {
    setEdgateSubject(null)
    setEdgateStandards(null)
  }, [state, kiddomSubject, setEdgateSubject, setEdgateStandards])

  // Update mapping when profile data is available
  React.useEffect(() => {
    if (!profile || !state || !kiddomSubject) {
      setEdgateSubject(null)
      return
    }

    // Find EdGate subject mapping based on selected subject
    if (profile.data.mappings?.edgate_subjects) {
      const subjectMapping = Object.entries(
        profile.data.mappings.edgate_subjects,
      ).find(([, subject]) => subject.kiddom_name === kiddomSubject)
      setEdgateSubject(subjectMapping ? subjectMapping[0] : null) // The key is the EdGate subject name
    } else {
      setEdgateSubject(null)
    }
  }, [profile, state, kiddomSubject, setEdgateSubject])

  // Fetch standards when both state and edgateSubject are available
  React.useEffect(() => {
    if (state && edgateSubject && !edgateStandards && !loadingEdgateStandards) {
      fetchEdgateStandards(state, edgateSubject).then((result) => {
        if (result) {
          setEdgateStandards(result)
        }
      })
    }
  }, [
    state,
    edgateSubject,
    edgateStandards,
    loadingEdgateStandards,
    fetchEdgateStandards,
    setEdgateStandards,
  ])

  // Check if profile is loading
  if (!profile || loadingProfile) {
    return renderLoading('Loading profile...')
  }

  // Check if EdGate standards data is available
  if (!state || !edgateSubject) {
    return renderMessage('Select state and subject')
  }

  // Check if standards are loading or not loaded yet
  if (!edgateStandards || loadingEdgateStandards) {
    return renderLoading(`Loading standards for ${state} ${edgateSubject}`)
  }

  return <EdgateStandardsView state={state} />
}
